import {useCallback, useEffect, useRef, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import DocumentActions from 'notebook/documents/DocumentActions';
import DocumentStore from 'notebook/documents/DocumentStore';
import notifications from 'notifications';
import {ProcedureStepGroup} from 'procedures/ProcedureDomainTypes';
import removeNulls from 'util/removeNulls';

/**
 * Custom hook to load all documents for steps in a method.  Only fetches documents when the method changes.
 *
 * @returns True when step documents are loaded, false while loading or failed
 */
export function useLoadAllDocumentsInMethod(
  methodCid: string,
  stepGroups: Array<ProcedureStepGroup>
): boolean {
  const [stepDocumentsLoaded, setStepDocumentsLoaded] = useState(false);

  // We only want the useEffect to run when the method changes, so using a ref to avoid adding stepGroups to dependencies
  const stepGroupsRef = useRef(stepGroups);
  stepGroupsRef.current = stepGroups;

  const loadAllDocumentsInMethod = useCallback(async (): Promise<void> => {
    setStepDocumentsLoaded(false);

    const allDocumentIds = removeNulls(
      stepGroupsRef.current.flatMap((stepGroup) => stepGroup.steps.map((step) => step.documentId))
    );

    if (allDocumentIds.length === 0) {
      setStepDocumentsLoaded(true);
      return;
    }

    // Trigger a fresh fetch for all documents
    const fetchedAll = await DocumentActions.openMany(allDocumentIds);
    if (!fetchedAll) {
      return;
    }

    const loadedDocs = removeNulls(allDocumentIds.map((docId: string) => DocumentStore.getDocument(docId)));

    if (loadedDocs.length !== allDocumentIds.length) {
      const missingDocIds = allDocumentIds.filter((id) => DocumentStore.getDocument(id) == null);
      notifications.error('Failed to load all documents, please refresh the page.', {
        extra: `${missingDocIds.join(', ')}`,
      });
      return;
    }

    setStepDocumentsLoaded(true);
  }, []);

  useEffect(() => {
    if (BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS) {
      loadAllDocumentsInMethod();
    }
  }, [methodCid, loadAllDocumentsInMethod]);

  return stepDocumentsLoaded;
}
